// Design fixtures for per-project workflows. Nothing here talks to the API yet —
// this file fixes the SHAPE of a workflow so the screen can be argued about
// before any of it is built.
//
// The model is one sentence: WHEN something happens to a lead, DO these steps in
// order, and STOP when one of these things is true.
//
// That is the whole model. There is no canvas, no branching, no agent deciding
// its own order. A rule you can read out loud in one breath is a rule people
// will actually switch on, and the ordered-steps shape is the one every CRM
// user already understands from email sequences.

// No colors or icons live in here on purpose: this file has to stay runnable
// under plain `node` for its check, and colors belong to theme/palette.js
// anyway. The panel maps action ids to fills and icons.

// ── Triggers ──────────────────────────────────────────────────────────────
// Each trigger optionally takes ONE parameter. One is the ceiling on purpose:
// two parameters and the sentence stops being readable.
export const TRIGGERS = [
  { id: "created", label: "Lead is added", takes: null, hint: "manual, import, Meta ads or API" },
  { id: "status", label: "Status becomes", takes: "status", hint: "when someone moves the lead" },
  { id: "noReply", label: "No reply for", takes: "days", hint: "silence across every channel" },
  { id: "overdue", label: "Follow-up overdue by", takes: "days", hint: "reads the follow-up date on the lead" },
  { id: "valueAbove", label: "Deal value above", takes: "amount", hint: "so the big ones get the careful treatment" },
];

export const LEAD_STATUSES = ["open", "cold", "warm", "hot", "won", "lost"];

// ── Actions ───────────────────────────────────────────────────────────────
// `does` is the phrasing used inside the editor, `label` is the short form that
// fits in a row chip.
export const ACTIONS = [
  { id: "whatsapp", label: "WhatsApp", does: "Send a WhatsApp message" },
  { id: "email", label: "Email", does: "Send an email" },
  { id: "pdf", label: "PDF", does: "Write a PDF and send it" },
  { id: "call", label: "Call", does: "Give the owner a call task" },
  { id: "meeting", label: "Meeting", does: "Ask for a meeting" },
  { id: "task", label: "Task", does: "Create a task for the owner" },
];

export const actionOf = (id) => ACTIONS.find((a) => a.id === id) || ACTIONS[1];

// ── Stop conditions ───────────────────────────────────────────────────────
// Every workflow needs at least one, otherwise it keeps messaging a lead who
// already answered — the single fastest way to lose trust in automation.
export const STOPS = [
  { id: "reply", label: "They reply" },
  { id: "meeting", label: "A meeting gets booked" },
  { id: "closed", label: "Lead is marked won or lost" },
  { id: "lastStep", label: "All the steps are done" },
];

// ── Plain-English renderers ───────────────────────────────────────────────

export function describeTrigger(t) {
  const def = TRIGGERS.find((x) => x.id === t?.event);
  if (!def) return "—";
  if (def.takes === "status") return `${def.label} ${t.status || "…"}`;
  if (def.takes === "days") return `${def.label} ${t.days ?? "…"} ${t.days === 1 ? "day" : "days"}`;
  if (def.takes === "amount") return `${def.label} ${t.amount ? t.amount.toLocaleString("en-IN") : "…"}`;
  return def.label;
}

// Cumulative day offset of each step, so a row can say "runs over 9 days"
// instead of making someone add up the waits in their head.
export function stepSchedule(steps = []) {
  let day = 0;
  return steps.map((s) => {
    day += Number(s.waitDays) || 0;
    return day;
  });
}

export function totalDays(steps = []) {
  const sched = stepSchedule(steps);
  return sched.length ? sched[sched.length - 1] : 0;
}

// "Right away" / "next day" / "3 days later" — relative to the step before it,
// which is how people describe a sequence when they say it out loud.
export function describeWait(waitDays) {
  const d = Number(waitDays) || 0;
  if (d === 0) return "right away";
  if (d === 1) return "next day";
  return `${d} days later`;
}

export function describeStops(ids = []) {
  const labels = ids.map((id) => STOPS.find((s) => s.id === id)?.label).filter(Boolean);
  if (!labels.length) return "Never stops — add a stop rule.";
  return labels.join(", or ");
}

let seq = 0;
const uid = (p) => `${p}-${(seq += 1)}`;

export const makeStep = (action = "email") => ({
  id: uid("step"),
  action,
  text: "",
  waitDays: 0,
});

export function makeRule() {
  return {
    id: uid("wf"),
    name: "New workflow",
    enabled: false,
    trigger: { event: "created" },
    steps: [makeStep("whatsapp")],
    stops: ["reply", "closed"],
  };
}

// ── Seeds ─────────────────────────────────────────────────────────────────
// Five deliberately different shapes: one on arrival, one on a status change,
// one on silence, one on a lead everybody else wrote off, one after the win.

export const WORKFLOWS = [
  {
    id: "wf-first",
    name: "First contact",
    enabled: true,
    trigger: { event: "created" },
    steps: [
      { id: "s1", action: "whatsapp", waitDays: 0, text: "Two lines, casual, first person. Say who I am and that I just emailed. Ask nothing, send no links, never mention price." },
      { id: "s2", action: "email", waitDays: 0, text: "Read their website, work out what they sell and to whom, then write five lines naming one specific thing I noticed. No pitch — just ask if it's relevant." },
      { id: "s3", action: "pdf", waitDays: 2, text: "One page: the three gaps we would fix on their site, each with expected uplift. Specific enough that it could only have been written for them." },
      { id: "s4", action: "meeting", waitDays: 0, text: "Reply on the same thread with the strongest finding in the first sentence, then ask for 15 minutes this week." },
    ],
    stops: ["reply", "meeting", "closed"],
  },
  {
    id: "wf-hot",
    name: "Hot lead, move fast",
    enabled: true,
    trigger: { event: "status", status: "hot" },
    steps: [
      { id: "s1", action: "call", waitDays: 0, text: "Call task for the lead owner due in 2 hours, with a three-bullet brief: what they said, what they care about, the one question to ask." },
      { id: "s2", action: "email", waitDays: 1, text: "Proposal priced off their volume tier. Mirror the words they used for their own problem. Nothing beyond the agreed commercial structure." },
      { id: "s3", action: "email", waitDays: 2, text: "The closest case study as numbers only — before, after, timeframe. One paragraph, no adjectives." },
      { id: "s4", action: "whatsapp", waitDays: 2, text: "One line asking whether the proposal landed. Offer to walk them through it instead of them reading it." },
    ],
    stops: ["reply", "closed"],
  },
  {
    id: "wf-quiet",
    name: "Going quiet",
    enabled: true,
    trigger: { event: "noReply", days: 7 },
    steps: [
      { id: "s1", action: "email", waitDays: 0, text: "Lead with whatever shipped on our side since we last wrote, but only if it touches what they cared about. Make it easy to say no." },
      { id: "s2", action: "whatsapp", waitDays: 4, text: "One line. Ask straight out whether to keep this open or close it. No pitch, no guilt." },
    ],
    stops: ["reply", "lastStep"],
  },
  {
    id: "wf-revive",
    name: "Lost, but worth another look",
    enabled: false,
    trigger: { event: "status", status: "lost" },
    steps: [
      { id: "s1", action: "email", waitDays: 30, text: "Read the lost reason first. If nothing has changed that answers it, send nothing at all. If it has: three lines — what they objected to, what changed, one question. Never mention the old deal value." },
    ],
    stops: ["reply", "lastStep"],
  },
  {
    id: "wf-onboard",
    name: "Won, get them live",
    enabled: true,
    trigger: { event: "status", status: "won" },
    steps: [
      { id: "s1", action: "email", waitDays: 0, text: "Who owns their account, the three things we need from them, and what we will have done by day seven. Warm, short, no marketing language." },
      { id: "s2", action: "task", waitDays: 0, text: "Kickoff task for the account owner due within 24 hours, agenda pre-filled from what was agreed during the deal." },
      { id: "s3", action: "pdf", waitDays: 3, text: "Scope as sold, named owners on both sides, the week-by-week plan, and what we need from them by when." },
      { id: "s4", action: "email", waitDays: 7, text: "Summarise what actually got done in week one from their activity, then ask the one question most likely to surface a problem early." },
    ],
    stops: ["closed", "lastStep"],
  },
];

// ── History ───────────────────────────────────────────────────────────────
// What each workflow has actually done, which is the only honest answer to "is
// this thing working". One entry per lead touched, each carrying its own list of
// touches. Nothing is counted here: every number in the panel comes out of
// summariseHistory(), so a stat can never drift from the rows underneath it.

export const OUTCOMES = {
  meeting: { label: "Meeting booked", tone: "success", done: true, win: true },
  replied: { label: "Replied", tone: "success", done: true, win: true },
  won: { label: "Won", tone: "success", done: true, win: true },
  working: { label: "Still working", tone: "info", done: false, win: false },
  parked: { label: "No reply, parked", tone: "neutral", done: true, win: false },
  handed: { label: "Handed to owner", tone: "warning", done: true, win: false },
  quiet: { label: "Stayed quiet", tone: "neutral", done: true, win: false },
};

// `by` says who did it, which is the difference between "this was sent
// automatically" and "it asked and you approved it" — the thing people want to
// audit first.
const touch = (action, label, at, note, by = "auto") => ({ action, label, at, note, by });

export const HISTORY = {
  "wf-first": [
    {
      id: "h1", lead: "Aarav Textiles", outcome: "meeting", started: "28 Jul",
      touches: [
        touch("whatsapp", "WhatsApp hello", "28 Jul, 09:14", "Two lines, no link — they had just arrived from a Meta ad."),
        touch("email", "Researched intro", "28 Jul, 09:16", "Read aaravtextiles.com and led with their 4-colour rotary line in Surat."),
        touch("pdf", "Site teardown", "30 Jul, 11:02", "Built the one-pager and held it for you, because this step needs approval.", "approved"),
        touch("meeting", "Asked for 15 minutes", "30 Jul, 14:20", "Opened with the buried enquiry form finding, then asked for Thursday."),
        touch("stop", "They booked", "31 Jul, 10:40", "15 minutes on Thursday. Status set to hot, handed to Priya.", "lead"),
      ],
    },
    {
      id: "h2", lead: "Nordic Kitchenware", outcome: "working", started: "6 Aug",
      touches: [
        touch("whatsapp", "WhatsApp hello", "6 Aug, 18:02", "Arrived 40 minutes earlier, so this went out first."),
        touch("wait", "Waiting", "next: tomorrow 09:00", "Holding the intro email until morning — quiet hours are on."),
      ],
    },
    {
      id: "h3", lead: "Brightline Optics", outcome: "replied", started: "2 Aug",
      touches: [
        touch("email", "Researched intro", "2 Aug, 08:30", "No WhatsApp number on the lead, so it skipped straight to email."),
        touch("stop", "They answered", "2 Aug, 15:11", "“Send me something on the freight side.” Stopped and handed over.", "lead"),
      ],
    },
    {
      id: "h4", lead: "Zayn Home Decor", outcome: "parked", started: "18 Jul",
      touches: [
        touch("whatsapp", "WhatsApp hello", "18 Jul, 10:00", "Delivered, never read."),
        touch("email", "Researched intro", "18 Jul, 10:01", "Opened twice, no reply."),
        touch("pdf", "Site teardown", "21 Jul, 09:30", "Sent proof instead of another nudge."),
        touch("meeting", "Asked for 15 minutes", "24 Jul, 09:30", "Still nothing."),
        touch("stop", "Parked as cold", "24 Jul, 09:31", "Last step done with no reply, so it stopped."),
      ],
    },
    {
      id: "h5", lead: "Copper & Clay", outcome: "handed", started: "22 Jul",
      touches: [
        touch("whatsapp", "WhatsApp hello", "22 Jul, 11:20", "They replied asking us to circle back after Diwali."),
        touch("stop", "Handed to owner", "22 Jul, 11:44", "A date was named, so it stopped guessing and gave it to you."),
      ],
    },
  ],

  "wf-hot": [
    {
      id: "h6", lead: "Vertex Furniture", outcome: "working", started: "5 Aug",
      touches: [
        touch("call", "Call task for Priya", "5 Aug, 12:05", "Due in 2 hours, with the three-bullet brief."),
        touch("email", "Tailored proposal", "6 Aug, 17:30", "Drafted on their volume tier. Waiting on your approval before it sends.", "waiting"),
      ],
    },
    {
      id: "h7", lead: "Saffron Foods", outcome: "won", started: "12 Jul",
      touches: [
        touch("call", "Call task for Priya", "12 Jul, 09:10", "Called inside the hour."),
        touch("email", "Tailored proposal", "13 Jul, 16:00", "Priced off their volume tier, mirrored their own words on wastage.", "approved"),
        touch("whatsapp", "Did it land?", "16 Jul, 10:00", "Proposal had not been opened. One line, no pitch."),
        touch("stop", "Signed", "18 Jul, 15:22", "Status set to won, so this stopped and the onboarding workflow picked it up."),
      ],
    },
    {
      id: "h8", lead: "Ridge Outdoor", outcome: "replied", started: "1 Aug",
      touches: [
        touch("call", "Call task for Priya", "1 Aug, 14:00", "Call happened, scope still vague."),
        touch("email", "Category proof", "3 Aug, 09:00", "Numbers only — before, after, timeframe."),
        touch("stop", "They answered", "3 Aug, 19:40", "Asked for a month. Stopped and left the follow-up on 3 Sep.", "lead"),
      ],
    },
  ],

  "wf-quiet": [
    {
      id: "h9", lead: "Mint & Co", outcome: "replied", started: "20 Jul",
      touches: [
        touch("email", "Whatever changed since", "20 Jul, 10:00", "Led with the freight lane we opened, because that is what they cared about."),
        touch("whatsapp", "One-line close", "24 Jul, 10:00", "Asked straight out whether to keep it open."),
        touch("stop", "They answered", "24 Jul, 12:15", "“Keep it open, Q4.” Handed back to the owner.", "lead"),
      ],
    },
    {
      id: "h10", lead: "Halcyon Living", outcome: "parked", started: "14 Jul",
      touches: [
        touch("email", "Whatever changed since", "14 Jul, 10:00", "Nothing new on our side that touched their problem, so it kept it short."),
        touch("whatsapp", "One-line close", "18 Jul, 10:00", "No answer."),
        touch("stop", "Parked as cold", "18 Jul, 10:01", "Both steps done, nothing back."),
      ],
    },
    {
      id: "h11", lead: "Ridge Outdoor", outcome: "working", started: "7 Aug",
      touches: [
        touch("email", "Whatever changed since", "7 Aug, 09:00", "Sent."),
        touch("wait", "Waiting", "next: in 2 days", "Four days between the email and the one-line close."),
      ],
    },
  ],

  "wf-revive": [
    {
      id: "h12", lead: "Halcyon Living", outcome: "quiet", started: "4 Aug",
      touches: [
        touch("stop", "Sent nothing, on purpose", "4 Aug, 08:00", "Lost on price. What changed was a feature release, which does not answer price, so it said so rather than writing anyway."),
      ],
    },
    {
      id: "h13", lead: "Pine & Pepper", outcome: "replied", started: "12 Jun",
      touches: [
        touch("email", "The reason has changed", "12 Jun, 09:30", "They walked over minimum order size. That floor moved, so it led with exactly that.", "approved"),
        touch("stop", "They answered", "12 Jun, 21:05", "“That changes things.” Reopened as warm.", "lead"),
      ],
    },
  ],

  "wf-onboard": [
    {
      id: "h14", lead: "Saffron Foods", outcome: "won", started: "18 Jul",
      touches: [
        touch("email", "Welcome + what happens next", "18 Jul, 15:40", "Named their account owner and the three things we needed."),
        touch("task", "Kickoff on the calendar", "18 Jul, 15:41", "Agenda pre-filled from what was agreed during the deal."),
        touch("pdf", "Onboarding pack", "21 Jul, 11:00", "Scope as sold, owners on both sides, week-by-week plan.", "approved"),
        touch("email", "Week one check-in", "25 Jul, 09:00", "Summarised what actually shipped, asked the one awkward question."),
        touch("stop", "Onboarded", "25 Jul, 18:00", "Access live, week one happened. Stopped."),
      ],
    },
    {
      id: "h15", lead: "Peak Athletics", outcome: "working", started: "4 Aug",
      touches: [
        touch("email", "Welcome + what happens next", "4 Aug, 12:00", "Sent within the hour of closing."),
        touch("task", "Kickoff on the calendar", "4 Aug, 12:01", "Done, kickoff was yesterday."),
        touch("pdf", "Onboarding pack", "7 Aug, 16:00", "Drafted from the confirmed scope. Waiting on your approval.", "waiting"),
      ],
    },
    {
      id: "h16", lead: "Casa Verde", outcome: "handed", started: "29 Jul",
      touches: [
        touch("email", "Welcome + what happens next", "29 Jul, 10:00", "Sent."),
        touch("task", "Kickoff on the calendar", "29 Jul, 10:01", "Booked, then cancelled by them."),
        touch("stop", "Escalated to the account owner", "3 Aug, 09:00", "Five days dark. This is a person's problem, not a workflow's."),
      ],
    },
  ],
};

// Everything the summary strip shows, counted off the rows themselves.
export function summariseHistory(runs = []) {
  const byOutcome = {};
  let touches = 0;
  let waiting = 0;
  for (const r of runs) {
    byOutcome[r.outcome] = (byOutcome[r.outcome] || 0) + 1;
    // A wait or a deliberate silence is not a touch — counting it would inflate
    // "messages sent" with messages nobody sent.
    touches += r.touches.filter((t) => t.action !== "wait" && t.action !== "stop").length;
    if (r.touches.some((t) => t.by === "waiting")) waiting += 1;
  }
  const wins = runs.filter((r) => OUTCOMES[r.outcome]?.win).length;
  const open = runs.filter((r) => !OUTCOMES[r.outcome]?.done).length;
  return { leads: runs.length, touches, wins, open, waiting, byOutcome };
}

// The one line at the top of the history panel. Has to survive a brand new
// workflow, which should say so rather than render "0 of 0".
export function historyHeadline(sum) {
  if (!sum.leads) return "This workflow hasn't run yet.";
  const n = (c, one, many) => `${c} ${c === 1 ? one : many}`;
  const parts = [`Worked on ${n(sum.leads, "lead", "leads")}`];
  if (sum.touches) parts.push(`sent ${n(sum.touches, "message", "messages")}`);
  if (sum.wins) parts.push(`${sum.wins} came back`);
  return `${parts.join(", ")}.`;
}
